import classNames from "classnames";
import { useState, MouseEvent, useRef } from "react";
import {
  arrayMode,
  getSelectedItemsUuids,
  selectionBoxPositionStyle,
  useZoom,
} from "./dnd_helpers";
import { cloneDeep } from "lodash";

type Position = {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
};

export type DraggableItem = {
  uuid: string;
  position: Position;
};

type DragAction =
  | "move"
  | "resize-top"
  | "resize-right"
  | "resize-bottom"
  | "resize-left"
  | "selection";

type DragState = Partial<{
  startPoint: { x: number; y: number };
  delta: { x: number; y: number };
  action: DragAction;
  targets: string[];
  selected: string[];
}>;

export const DraggableCanvas = ({
  children,
  boxes,
  handleUpdates,
}: {
  children: React.ReactNode;
  boxes: {
    model: string;
    items: DraggableItem[];
    render: (item: DraggableItem, zoom: number, isSelected: boolean) => React.ReactNode;
  };
  handleUpdates: (actions: { item: DraggableItem; position: Position }[]) => void;
  className?: string;
}) => {
  const [zoom, setZoom] = useState(1);
  const [drag, setDrag] = useState<DragState>({});
  const selectionBoxRef = useRef<HTMLDivElement>(null);
  useZoom(setZoom);

  const hasDelta = drag?.delta?.x || drag?.delta?.y;

  const startDrag = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = (e.target as HTMLElement).closest("[data-dnd-role]") as HTMLElement;
    const role = target?.dataset.dndRole;
    const dragState: DragState = {
      startPoint: { x: e.clientX, y: e.clientY },
      delta: { x: 0, y: 0 },
    };

    if (role === "canvas") {
      dragState.action = "selection";
    } else if (role === "move" || role.startsWith("resize-")) {
      const itemUuid = (target.closest("[data-uuid]") as HTMLElement).dataset.uuid;
      const isSelected = drag.selected?.includes(itemUuid);
      dragState.action = role as DragAction;
      dragState.selected = isSelected ? drag.selected : [];
      dragState.targets = isSelected ? drag.selected : [itemUuid];
    }
    setDrag({ ...dragState });
  };

  const mouseMove = (e: MouseEvent) => {
    if (drag.startPoint) {
      setDrag({
        ...drag,
        delta: {
          x: (e.clientX - drag.startPoint.x) / zoom,
          y: (e.clientY - drag.startPoint.y) / zoom,
        },
      });
    }
  };

  const dragEnd = (e: MouseEvent) => {
    if (drag.action === "selection") {
      setDrag({ selected: getSelectedItemsUuids(selectionBoxRef.current) });
    } else if (hasDelta) {
      handleUpdates(
        drag.targets?.map((uuid) => {
          const item = boxes.items.find((item) => item.uuid === uuid)!;
          const position = applyDragToPosition(item.position, drag.action, drag.delta);
          return { item, position };
        }) ?? []
      );
      setDrag({ selected: drag.selected });
    } else {
      setDrag({});
    }
  };

  const round = (value: number, step = 5) => Math.round(value / step) * step;

  const applyDragToPosition = (
    position: Position,
    action: DragAction,
    delta: { x: number; y: number }
  ) => {
    const p = { ...position };

    if (action === "move") {
      p.left = Math.max(round(p.left + delta.x), 10);
      p.top = Math.max(round(p.top + delta.y), 10);
    } else if (action === "resize-right") {
      p.width = Math.max(round(p.width + delta.x), 50);
    } else if (action === "resize-bottom") {
      p.height = Math.max(round(p.height + delta.y), 50);
    } else if (action === "resize-left") {
      p.left = Math.max(round(p.left + delta.x), 50);
      p.width = Math.max(round(p.width - delta.x), 50);
    } else if (action === "resize-top") {
      p.top = Math.max(round(p.top + delta.y), 50);
      p.height = Math.max(round(p.height - delta.y), 50);
    }

    return p;
  };

  const applyPositionStyle = (item: DraggableItem) => {
    const isDragTarget = drag.targets?.includes(item.uuid);
    let out = { ...item.position };

    // drag
    if (isDragTarget && hasDelta) {
      out = applyDragToPosition(out, drag.action, drag.delta);
    }

    // zoom
    Object.keys(out).forEach((key) => {
      out[key] = out[key] * zoom;
    });

    return out;
  };

  const getSelectedItems = () => {
    return drag.selected?.map((uuid) => boxes.items.find((item) => item.uuid === uuid)!);
  };

  const makeSameSize = () => {
    const selectedItems = getSelectedItems();
    const width = arrayMode(selectedItems.map((item) => item.position.width));
    const height = arrayMode(selectedItems.map((item) => item.position.height));
    handleUpdates(selectedItems.map((item) => ({ item, position: { width, height } })));
  };

  const align = () => {
    const selectedItems = getSelectedItems();
    const top = arrayMode(selectedItems.map((item) => item.position.top));
    handleUpdates(selectedItems.map((item) => ({ item, position: { top } })));
  };

  const spreadEvenly = () => {
    const selectedItems = getSelectedItems();
    if (!selectedItems?.length) return;

    const sortedItems = cloneDeep(selectedItems).sort(
      (a, b) => a.position.left - b.position.left
    );

    sortedItems.forEach((item, i) => {
      if (i > 0) {
        item.position.left =
          sortedItems[i - 1].position.left + sortedItems[i - 1].position.width + 20;
      }
    });

    handleUpdates(
      sortedItems.map((item) => ({
        item,
        position: { left: item.position.left },
      }))
    );
  };

  const selectionBoxStyle = selectionBoxPositionStyle(drag, selectionBoxRef.current);

  return (
    <div className="site-map-canvas-outer position-relative">
      {drag.selected?.length > 2 && (
        <div className="position-fixed" style={{ top: 100, right: 75 }}>
          {[
            { onClick: makeSameSize, icon: "bi-bounding-box" },
            { onClick: align, icon: "bi-align-top" },
            { onClick: spreadEvenly, icon: "bi-distribute-horizontal" },
          ].map(({ onClick, icon }) => (
            <i
              key={icon}
              className={`btn btn-sm btn-outline-secondary mh-1 bi ${icon}`}
              onClick={onClick}
            />
          ))}
        </div>
      )}
      <div
        className="site-map-canvas position-relative position-relative"
        data-dnd-role="canvas"
        onMouseMove={mouseMove}
        onMouseUp={dragEnd}
        onMouseDown={startDrag}
      >
        {children}

        {boxes.items.map((item) => (
          <div
            key={item.uuid}
            style={{
              ...applyPositionStyle(item),
              pointerEvents: hasDelta ? "none" : "auto",
            }}
            onMouseDown={startDrag}
            className={classNames("position-absolute", {})}
            data-dnd-role="move"
            data-uuid={item.uuid}
          >
            {["left", "right", "top", "bottom"].map((side) => (
              <div
                key={side}
                className={`resize-handle ${side}`}
                data-dnd-role={`resize-${side}`}
                onMouseDown={startDrag}
              />
            ))}
            {boxes.render(item, zoom, drag.selected?.includes(item.uuid))}
          </div>
        ))}

        <div
          className="site-map-canvas-selection"
          style={selectionBoxStyle}
          data-dnd-role="selection"
          ref={selectionBoxRef}
        />
      </div>
    </div>
  );
};
